// @ts-check

/**
 * Isomorphic validation and grouping rules for collected browser errors.
 *
 * Kept free of DOM and network concerns so the Worker can re-validate whatever a
 * browser sends: the client is never trusted to supply its own grouping key.
 */

/** Sources allowed to submit an error report. */
export const ERROR_REPORT_SOURCES = ['client', 'admin', 'server'];

/** How a report was produced. `manual` covers explicit reportError() calls in catch blocks. */
export const ERROR_REPORT_KINDS = ['error', 'unhandledrejection', 'manual', 'oauth'];

/**
 * Maximum stored length per field. Oversized values are truncated rather than
 * rejected, because a giant stack is still the most useful part of a bug report.
 */
export const ERROR_REPORT_LIMITS = {
  message: 500,
  stack: 4000,
  pageUrl: 500,
  userAgent: 300,
  appVersion: 40,
  userId: 128,
  roomId: 16
};

/**
 * @typedef {Object} ErrorReport
 * @property {string} source - One of ERROR_REPORT_SOURCES.
 * @property {string} kind - One of ERROR_REPORT_KINDS.
 * @property {string} message - Error message, truncated.
 * @property {string} stack - Stack trace, truncated. May be empty.
 * @property {string} pageUrl - Page the error occurred on.
 * @property {string} userAgent - Reporting browser user agent.
 * @property {string} appVersion - Client build identifier, when known.
 * @property {string} userId - Reporting user id, when known.
 * @property {string} roomId - Active room code, when known.
 */

/**
 * Trims a value to a string and clamps it to a maximum length.
 *
 * @param {any} value
 * @param {number} max
 * @returns {string}
 */
function clamp(value, max) {
  return String(value ?? '').trim().slice(0, max);
}

/**
 * Reads a field accepting both camelCase and snake_case keys, matching the
 * convention used by the other public ingest endpoints.
 *
 * @param {Record<string, any>} raw
 * @param {string} camel
 * @param {string} snake
 * @returns {any}
 */
function field(raw, camel, snake) {
  return raw?.[camel] ?? raw?.[snake];
}

/**
 * Validates and normalizes a raw error report body.
 *
 * @param {any} raw - Untrusted request body.
 * @returns {ErrorReport|null} Normalized report, or null when unusable.
 */
export function normalizeErrorReport(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const message = clamp(raw.message, ERROR_REPORT_LIMITS.message);
  if (!message) return null;

  const rawSource = clamp(raw.source, 32).toLowerCase();
  const rawKind = clamp(raw.kind, 32).toLowerCase();

  return {
    source: ERROR_REPORT_SOURCES.includes(rawSource) ? rawSource : ERROR_REPORT_SOURCES[0],
    kind: ERROR_REPORT_KINDS.includes(rawKind) ? rawKind : ERROR_REPORT_KINDS[0],
    message,
    stack: clamp(raw.stack, ERROR_REPORT_LIMITS.stack),
    pageUrl: clamp(field(raw, 'pageUrl', 'page_url'), ERROR_REPORT_LIMITS.pageUrl),
    userAgent: clamp(field(raw, 'userAgent', 'user_agent'), ERROR_REPORT_LIMITS.userAgent),
    appVersion: clamp(field(raw, 'appVersion', 'app_version'), ERROR_REPORT_LIMITS.appVersion),
    userId: clamp(field(raw, 'userId', 'user_id'), ERROR_REPORT_LIMITS.userId),
    roomId: clamp(field(raw, 'roomId', 'room_id'), ERROR_REPORT_LIMITS.roomId).toUpperCase()
  };
}

/**
 * Extracts the first stack frame, skipping the leading `Error: message` line.
 *
 * @param {string} stack
 * @returns {string} The topmost frame, or an empty string.
 */
export function topStackFrame(stack) {
  const lines = String(stack || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.find((line) => line.startsWith('at ') || line.includes('@')) || lines[1] || '';
}

/**
 * Strips the parts of a frame that change without the underlying bug changing:
 * the origin, query strings, and the content hash Vite bakes into asset names.
 * Without this, one bug fragments into a new group on every deploy.
 *
 * @param {string} frame
 * @returns {string}
 */
function stableFrame(frame) {
  return String(frame || '')
    .replace(/https?:\/\/[^/)\s]+/g, '')
    .replace(/\?[^)\s]*/g, '')
    .replace(/-[A-Za-z0-9_]{6,}\.(js|mjs|css)/g, '-*.$1')
    .toLowerCase();
}

/**
 * Computes a stable grouping key for a report. Deliberately excludes the page URL,
 * user, and timestamps so repeats of one bug collapse into a single row.
 *
 * @param {ErrorReport|null} report
 * @returns {string} Fingerprint of the form `ce_<base36>`.
 */
export function fingerprintErrorReport(report) {
  if (!report) return '';
  const basis = [
    report.source,
    report.kind,
    String(report.message).toLowerCase().replace(/\s+/g, ' '),
    stableFrame(topStackFrame(report.stack))
  ].join('|');

  // FNV-1a: short, dependency-free, and stable across runtimes.
  let hash = 0x811c9dc5;
  for (let i = 0; i < basis.length; i++) {
    hash ^= basis.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return 'ce_' + hash.toString(36);
}
